import { ApiService, ResponseType } from '@/support/ApiService';

/** File extension the SEND validator accepts (one CSV per SENDIG domain). */
export const CSV_EXTENSION = '.csv';

/** Upper bound on files in one bundle — mirrors the server's per-session cap. */
export const MAX_BUNDLE_FILES = 30;

/**
 * Uploads CSVs into a single server-minted batch. The server owns the S3 key (built from the team in
 * its security context plus the IDs it mints); the client only ever learns opaque IDs. The first
 * file presigns with no batch id so the server mints one; every other file must echo that id to join
 * the same bundle. Because the multi-file upload field uploads concurrently, this serialises only the
 * *batch acquisition* — the leader (first caller) resolves a shared batch-id promise the followers
 * await — while the file PUTs still run in parallel.
 *
 * The form stores the array of `attachment_api_id`s (one per successful upload) as the field value;
 * the batch id and the id→filename map are read off the uploader instance (detection returns only
 * attachment ids, so filenames for the confirm-row labels live here).
 */
export interface SendBundleUploader {
  /** Presigns + PUTs one CSV into the shared batch and resolves to its stored `attachment_api_id`. */
  uploadFile: (file: File, signal?: AbortSignal) => Promise<string>;
  /** The batch id once the leader has opened it, else null (detection needs it to create the session). */
  batchApiId: () => string | null;
  /** Original filename recorded for an uploaded attachment id, for labelling confirm rows. */
  fileNameFor: (attachmentApiId: string) => string | null;
}

interface Deferred {
  promise: Promise<string>;
  resolve: (batchApiId: string) => void;
  reject: (reason: unknown) => void;
}

const makeDeferred = (): Deferred => {
  let resolve: (batchApiId: string) => void = () => undefined;
  let reject: (reason: unknown) => void = () => undefined;
  const promise = new Promise<string>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // A leader presign failure rejects this promise; when no follower is awaiting (single file, or the
  // leader failed before any follower joined) that rejection would surface as an unhandled rejection.
  // A no-op handler marks it handled — followers that do await still receive the rejection.
  promise.catch(() => undefined);
  return { promise, resolve, reject };
};

/**
 * Creates a fresh {@link SendBundleUploader} bound to one bundle. Hold one instance per upload
 * session (e.g. per form mount) so every picked file lands in the same batch.
 */
export const createSendBundleUploader = (): SendBundleUploader => {
  // Null until the leader opens the batch. The synchronous null-check-then-assign below elects
  // exactly one leader even under the field's concurrent dispatch: JS runs each call's synchronous
  // prologue up to its first await without interleaving, so only the first sees `batch === null`.
  let batch: Deferred | null = null;
  let resolvedBatchApiId: string | null = null;
  const fileNames = new Map<string, string>();

  const put = async (url: string, file: File, signal?: AbortSignal): Promise<void> => {
    const putResponse = await fetch(url, { method: 'PUT', body: file, signal });
    if (!putResponse.ok) {
      throw new Error(`S3 upload failed (${putResponse.status})`);
    }
  };

  const uploadFile = async (file: File, signal?: AbortSignal): Promise<string> => {
    let isLeader = false;
    if (batch === null) {
      isLeader = true;
      batch = makeDeferred();
    }
    const currentBatch = batch;

    if (isLeader) {
      // First file: omit the batch id so the server mints one, then publish it to the followers.
      const presigned = await ApiService.call({
        endpoint: 'POST /api/v1/ai-agents/send-validator/uploads',
        body: { file_name: file.name },
        signal,
        options: {
          onError: { toast: false, throw: false, capture: true, slug: 'send-validator-presign-upload' },
        },
      });
      if (presigned.type !== ResponseType.Success) {
        currentBatch.reject(new Error('Could not mint a presigned upload URL.'));
        // Let a later pick (or retry) re-open the batch rather than wedging on the failed leader.
        batch = null;
        throw new Error('Could not mint a presigned upload URL.');
      }
      const { url, batch_api_id, attachment_api_id } = presigned.body;
      resolvedBatchApiId = batch_api_id;
      currentBatch.resolve(batch_api_id);
      await put(url, file, signal);
      fileNames.set(attachment_api_id, file.name);
      return attachment_api_id;
    }

    // Follower: wait for the leader to establish the batch, then join it.
    const batchApiId = await currentBatch.promise;
    const presigned = await ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator/uploads',
      body: { file_name: file.name, batch_api_id: batchApiId },
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'send-validator-presign-upload' },
      },
    });
    if (presigned.type !== ResponseType.Success) {
      throw new Error('Could not mint a presigned upload URL.');
    }
    const { url, attachment_api_id } = presigned.body;
    await put(url, file, signal);
    fileNames.set(attachment_api_id, file.name);
    return attachment_api_id;
  };

  return {
    uploadFile,
    batchApiId: () => resolvedBatchApiId,
    fileNameFor: (attachmentApiId) => fileNames.get(attachmentApiId) ?? null,
  };
};
